import {
  ASSESSMENT_STATUS_LABELS,
  formatDate,
  type AssessmentDto,
  type AssessmentTemplateDto,
  type OrganizationDto,
} from '@nova/shared';
import {
  BadgeCheck,
  ChevronDown,
  ExternalLink,
  FileSearch,
  History,
  Settings2,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../components/Button.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Field, SelectInput, TextArea, TextInput } from './marketWidgets.js';
import {
  useAssessmentHistory,
  useConfirmAssessment,
  useCreateTemplate,
  useKartlegg,
  useSuggestPrompt,
  useTemplateVersions,
} from './usePriority.js';

export function AssessmentPanel({ org }: { org: OrganizationDto }) {
  const products = useMemo(
    () => Array.from(new Map(org.holdings.map((h) => [h.productId, h.productName])).entries()),
    [org.holdings],
  );
  const [productId, setProductId] = useState<string>(products[0]?.[0] ?? '');
  const canEdit = useAuth().hasModule('SALES', 'MEMBER');
  const isAdmin = useAuth().hasModule('SALES', 'ADMIN');

  if (products.length === 0) {
    return (
      <section className="border-hairline space-y-2 border-t pt-4">
        <p className="label-micro text-ink-soft">Situasjonskartlegging</p>
        <p className="text-ink-soft text-xs">
          Registrer en produktbeholdning først, så kan Nova kartlegge organisasjonen mot produktet.
        </p>
      </section>
    );
  }

  return (
    <section className="border-hairline space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <p className="label-micro text-ink-soft">Situasjonskartlegging</p>
        {products.length > 1 && (
          <SelectInput
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="h-8 w-auto text-xs"
          >
            {products.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </SelectInput>
        )}
      </div>

      <AssessmentBody orgId={org.id} productId={productId} canEdit={canEdit} isAdmin={isAdmin} />
    </section>
  );
}

function AssessmentBody({
  orgId,
  productId,
  canEdit,
  isAdmin,
}: {
  orgId: string;
  productId: string;
  canEdit: boolean;
  isAdmin: boolean;
}) {
  const { data, isLoading } = useAssessmentHistory(orgId, productId);
  const kartlegg = useKartlegg(orgId, productId);
  const [instruction, setInstruction] = useState('');
  const [showInstruction, setShowInstruction] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showTemplate, setShowTemplate] = useState(false);

  if (isLoading || !data) return <p className="text-ink-soft text-xs">Laster kartlegging …</p>;

  const template = data.template;
  const selected = selectedId ? data.versions.find((v) => v.id === selectedId) ?? data.current : data.current;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && (
          <Button
            size="sm"
            variant="secondary"
            icon={<FileSearch size={14} />}
            loading={kartlegg.isPending}
            onClick={() => kartlegg.mutate(instruction.trim() || null, { onSuccess: () => setSelectedId(null) })}
          >
            Kartlegg
          </Button>
        )}
        {canEdit && (
          <button
            className="text-ink-soft hover:text-ink text-[11px]"
            onClick={() => setShowInstruction((v) => !v)}
          >
            {showInstruction ? 'Skjul styring' : 'Styr kartleggingen'}
          </button>
        )}
        {isAdmin && (
          <button
            className="text-ink-soft hover:text-ink ml-auto inline-flex items-center gap-1 text-[11px]"
            onClick={() => setShowTemplate((v) => !v)}
          >
            <Settings2 size={12} aria-hidden /> Mal
          </button>
        )}
      </div>

      {showInstruction && canEdit && (
        <Field label="Ekstra styring (valgfritt)" hint="F.eks. «legg vekt på universell utforming».">
          <TextArea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} />
        </Field>
      )}

      {kartlegg.isError && (
        <p className="text-danger bg-danger/10 rounded-lg px-3 py-2 text-xs">
          {(kartlegg.error as ApiError).message}
        </p>
      )}

      {isAdmin && showTemplate && <TemplateManager productId={productId} template={template} />}

      {!selected ? (
        <p className="text-ink-soft text-xs">
          Ingen kartlegging kjørt ennå. Trykk «Kartlegg» for å be Nova lage en agentvurdering.
        </p>
      ) : (
        <AssessmentView assessment={selected} template={template} orgId={orgId} productId={productId} canEdit={canEdit} />
      )}

      {data.versions.length > 1 && (
        <VersionHistory
          versions={data.versions}
          selectedId={selected?.id ?? null}
          onSelect={(id) => setSelectedId(id)}
        />
      )}
    </div>
  );
}

function AssessmentView({
  assessment,
  template,
  orgId,
  productId,
  canEdit,
}: {
  assessment: AssessmentDto;
  template: AssessmentTemplateDto | null;
  orgId: string;
  productId: string;
  canEdit: boolean;
}) {
  const labelFor = (key: string) => template?.questions.find((q) => q.key === key)?.label ?? key;
  const isAgent = assessment.status === 'AGENT';

  return (
    <div className="border-hairline bg-base/30 space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-ink text-sm font-medium">Versjon {assessment.version}</span>
        <StatusBadge status={assessment.status} />
      </div>

      {isAgent && (
        <p className="text-warning bg-warning/10 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] leading-relaxed">
          <TriangleAlert size={13} className="mt-0.5 shrink-0" aria-hidden />
          Agentvurdering — ikke bekreftet. Les kritisk og sjekk kildene før den brukes.
        </p>
      )}

      {assessment.fitScore != null && (
        <div className="text-xs">
          <span className="text-ink-soft">Passform: </span>
          <span className="text-ink font-medium">{assessment.fitScore}/100</span>
          {assessment.fitRationale && <span className="text-ink-soft"> — {assessment.fitRationale}</span>}
        </div>
      )}

      {assessment.freeText && (
        <p className="text-ink-soft text-xs leading-relaxed">{assessment.freeText}</p>
      )}

      <dl className="space-y-1.5">
        {Object.entries(assessment.result)
          .filter(([, v]) => v)
          .map(([key, value]) => (
            <div key={key}>
              <dt className="text-ink-soft/70 text-[10px] uppercase tracking-wide">{labelFor(key)}</dt>
              <dd className="text-ink text-xs leading-relaxed">{value}</dd>
            </div>
          ))}
      </dl>

      {assessment.sources.length > 0 && (
        <div>
          <p className="text-ink-soft/70 text-[10px] uppercase tracking-wide">Kilder</p>
          <ul className="mt-1 space-y-0.5">
            {assessment.sources.map((s, i) => (
              <li key={i}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent inline-flex items-center gap-1 text-[11px]"
                >
                  {s.label || s.url} <ExternalLink size={10} aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-ink-soft/60 text-[10px]">
        {assessment.status === 'CONFIRMED'
          ? `Bekreftet av ${assessment.confirmedByName ?? 'ukjent'}${assessment.confirmedAt ? ` · ${formatDate(assessment.confirmedAt)}` : ''}`
          : `${assessment.generatedByName ?? 'Nova'}${assessment.model ? ` (${assessment.model})` : ''} · ${formatDate(assessment.createdAt)}`}
      </p>

      {isAgent && canEdit && (
        <ConfirmForm assessment={assessment} orgId={orgId} productId={productId} />
      )}
    </div>
  );
}

function ConfirmForm({
  assessment,
  orgId,
  productId,
}: {
  assessment: AssessmentDto;
  orgId: string;
  productId: string;
}) {
  const confirm = useConfirmAssessment(orgId, productId);
  const [editing, setEditing] = useState(false);
  const [freeText, setFreeText] = useState(assessment.freeText ?? '');
  const [fitScore, setFitScore] = useState(assessment.fitScore != null ? String(assessment.fitScore) : '');

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          icon={<BadgeCheck size={14} />}
          loading={confirm.isPending}
          onClick={() => confirm.mutate({ id: assessment.id, body: {} })}
        >
          Bekreft
        </Button>
        <button className="text-ink-soft hover:text-ink text-[11px]" onClick={() => setEditing(true)}>
          Rediger og bekreft
        </button>
        {confirm.isError && <span className="text-danger text-xs">{(confirm.error as ApiError).message}</span>}
      </div>
    );
  }

  return (
    <div className="border-accent/30 bg-base/40 space-y-2 rounded-lg border p-2.5">
      <Field label="Helhetsvurdering">
        <TextArea rows={3} value={freeText} onChange={(e) => setFreeText(e.target.value)} />
      </Field>
      <Field label="Passform (0–100)">
        <TextInput
          inputMode="numeric"
          value={fitScore}
          onChange={(e) => setFitScore(e.target.value)}
          placeholder="valgfritt"
        />
      </Field>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={confirm.isPending}
          onClick={() =>
            confirm.mutate({
              id: assessment.id,
              body: {
                freeText: freeText.trim() || null,
                fitScore: fitScore.trim() ? Math.max(0, Math.min(100, Number(fitScore))) : null,
              },
            })
          }
        >
          Lagre og bekreft
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Avbryt
        </Button>
      </div>
    </div>
  );
}

function VersionHistory({
  versions,
  selectedId,
  onSelect,
}: {
  versions: AssessmentDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        className="text-ink-soft hover:text-ink inline-flex items-center gap-1 text-[11px]"
        onClick={() => setOpen((v) => !v)}
      >
        <History size={12} aria-hidden /> Versjonshistorikk ({versions.length})
        <ChevronDown size={12} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <ol className="mt-2 space-y-1">
          {versions.map((v) => (
            <li key={v.id}>
              <button
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-[11px]',
                  v.id === selectedId ? 'bg-accent/10 text-accent' : 'text-ink-soft hover:bg-base/40',
                )}
                onClick={() => onSelect(v.id)}
              >
                <span>
                  v{v.version} · {ASSESSMENT_STATUS_LABELS[v.status]}
                </span>
                <span>{formatDate(v.createdAt)}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: AssessmentDto['status'] }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
        status === 'CONFIRMED'
          ? 'bg-success/15 text-success border-success/30'
          : 'bg-warning/12 text-warning border-warning/25',
      )}
    >
      {status === 'CONFIRMED' ? <BadgeCheck size={11} aria-hidden /> : <TriangleAlert size={11} aria-hidden />}
      {ASSESSMENT_STATUS_LABELS[status]}
    </span>
  );
}

// ── Malforvaltning (SALES:ADMIN) ───────────────────────────────────────────────

function TemplateManager({
  productId,
  template,
}: {
  productId: string;
  template: AssessmentTemplateDto | null;
}) {
  const { data: versions } = useTemplateVersions(productId);
  const suggest = useSuggestPrompt();
  const create = useCreateTemplate();
  const [name, setName] = useState(template?.name ?? 'Kartleggingsmal');
  const [prompt, setPrompt] = useState(template?.suggestedPrompt ?? '');
  const [questions, setQuestions] = useState(
    template?.questions.map((q) => ({ key: q.key, label: q.label, hint: q.hint ?? '' })) ?? [],
  );

  function updateQuestion(i: number, patch: Partial<{ key: string; label: string; hint: string }>) {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }

  return (
    <div className="border-hairline bg-base/30 space-y-3 rounded-lg border p-3">
      <p className="text-ink text-xs font-medium">
        Kartleggingsmal{template ? ` (aktiv: v${template.version})` : ''}
      </p>

      <Field label="Malnavn">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="space-y-2">
        <p className="label-micro text-ink-soft">Spørsmål / dimensjoner</p>
        {questions.map((q, i) => (
          <div key={i} className="border-hairline grid grid-cols-2 gap-2 rounded-md border p-2">
            <TextInput
              className="h-8 text-xs"
              value={q.key}
              onChange={(e) => updateQuestion(i, { key: e.target.value })}
              placeholder="nøkkel"
            />
            <TextInput
              className="h-8 text-xs"
              value={q.label}
              onChange={(e) => updateQuestion(i, { label: e.target.value })}
              placeholder="spørsmål"
            />
            <TextInput
              className="col-span-2 h-8 text-xs"
              value={q.hint}
              onChange={(e) => updateQuestion(i, { hint: e.target.value })}
              placeholder="hjelpetekst (valgfritt)"
            />
          </div>
        ))}
        <button
          className="text-accent text-[11px]"
          onClick={() => setQuestions((qs) => [...qs, { key: '', label: '', hint: '' }])}
        >
          + Legg til spørsmål
        </button>
      </div>

      <Field label="Foreslått bestilling (prompt)">
        <TextArea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </Field>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          icon={<Sparkles size={13} />}
          loading={suggest.isPending}
          onClick={() =>
            suggest.mutate(
              { productId, instruction: null },
              { onSuccess: (r) => setPrompt(r.prompt) },
            )
          }
        >
          Be Nova foreslå
        </Button>
        {suggest.isError && <span className="text-danger text-xs">{(suggest.error as ApiError).message}</span>}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={create.isPending}
          disabled={questions.length === 0 || questions.some((q) => !q.key.trim() || !q.label.trim())}
          onClick={() =>
            create.mutate(
              {
                productId,
                name: name.trim(),
                questions: questions.map((q) => ({
                  key: q.key.trim(),
                  label: q.label.trim(),
                  hint: q.hint.trim() || null,
                })),
                suggestedPrompt: prompt.trim() || null,
                approvePrompt: true,
              },
            )
          }
        >
          Lagre som ny versjon
        </Button>
        {create.isSuccess && <span className="text-success text-xs">Ny versjon lagret.</span>}
        {create.isError && <span className="text-danger text-xs">{(create.error as ApiError).message}</span>}
      </div>

      {versions && versions.length > 0 && (
        <p className="text-ink-soft/60 text-[10px]">
          {versions.length} versjon(er). En ny versjon overskriver aldri en gammel.
        </p>
      )}
    </div>
  );
}
